import { useState, useCallback } from "react";
import apiClient from "../../../application/apis/tenant-management/tenant-management-client";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useAuthContextV2 } from "../../../auth-state";
import { reportUnexpectedError } from "../../../error-reporter";
import {
  isValidRedirect,
  parseQueryOfLocation,
} from "../../../utils/url-utils";
import {
  PrimaryButton,
  Spinner,
  TextField,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "../../../graphql/client";
import { toast } from "react-toastify";
import { matchType } from "../../../application/domain/base/base-type";
import { LoginTemplate } from "../../template/login-template";

type SsoLoginStepLiterals = "SSO_LOGIN" | "SSO_LOGIN_UNAVAILABLE";

export const SSOLoginPage = () => {
  const { slug: initialSlug, to } = parseQueryOfLocation(useLocation()) as {
    slug?: string;
    to?: string;
  };
  const history = useHistory();
  const authCtx = useAuthContextV2();
  const [step, setStep] = useState<SsoLoginStepLiterals>("SSO_LOGIN");
  const [slug, setSlug] = useState(initialSlug);
  const [isLoading, setIsLoading] = useState(false);

  const canLoginBySAML = useCallback(
    async (slug: string) => {
      setIsLoading(true);
      try {
        const res = await runGqlQueryV2(() =>
          gqlClient.orgSamlSettingExists({ slug }, gqlHeader("q")),
        );
        if (res.error) {
          throw res.error;
        }
        return !!res.data?.orgSamlSettingExists;
      } catch (e) {
        toast.error(
          "システムエラーが発生しました。システム管理者にお問い合わせください。",
        );
        reportUnexpectedError(e);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading],
  );

  const handleSubmit = useCallback(
    async (slug: string) => {
      const execSAMLLogin = await canLoginBySAML(slug);
      if (!execSAMLLogin) {
        setStep("SSO_LOGIN_UNAVAILABLE");
        return;
      }
      try {
        setIsLoading(true);
        const { data } = await apiClient.samlLogin(slug, to);
        window.location.href = data.redirectUrl;
      } catch (e) {
        toast.error(
          "SAMLログインに失敗しました。システム管理者にお問い合わせください。",
        );
        reportUnexpectedError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [canLoginBySAML, to],
  );

  if (authCtx.auth.isAuthed) {
    const decoded = to ? decodeURIComponent(to) : null;
    if (decoded && isValidRedirect(decoded)) {
      window.location.assign(decoded);
      return null;
    }
    return <Redirect to="/" />;
  }

  return (
    <LoginTemplate>
      <Spinner loading={isLoading} />
      {matchType(step, {
        SSO_LOGIN: (
          <form
            className="has-text-centered"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(slug!);
            }}
          >
            <div className="field my-5">
              <TextField
                label="組織コード"
                description={""}
                placeholder="組織コード (例 city-graffer)"
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={!!initialSlug && initialSlug.length > 0}
              />
            </div>
            <PrimaryButton
              text={"シングルサインオンでログインする"}
              disabled={!slug}
              type="submit"
            />
          </form>
        ),
        SSO_LOGIN_UNAVAILABLE: (
          <div className="has-text-centered">
            <div className="my-5">
              <WarningBanner
                message="シングルサインオンは利用できません。"
                description="組織にシングルサインオンが設定されていないため、シングルサインオンは利用できません。パスワードログインをご利用ください。"
              />
            </div>
            <PrimaryButton
              text="パスワードログインに戻る"
              onClick={() => history.push(`/login${location.search}`)}
            />
          </div>
        ),
      })}
    </LoginTemplate>
  );
};
